import type {
  LpsDeliveryOutcome,
  LpsForwardState,
  LpsLeadItem,
  LpsLeadStatus,
  LpsMarketingChannel,
  LpsSource,
} from './lps.api';
import type { UzBadgeProps } from '@utazzo/ui';

export const SOURCE_LABEL: Record<LpsSource, string> = {
  WEBSITE: 'Website',
  META_LEAD_ADS: 'Meta lead ads',
  WHATSAPP: 'WhatsApp',
  LANDING_PAGE: 'Landing page',
  /** Created by hand on the CRM lead form and mirrored in (connector `utazzo-crm`). */
  MANUAL: 'Utazzo CRM',
};

export const LEAD_STATUS_LABEL: Record<LpsLeadStatus, string> = {
  NEW: 'New',
  DUPLICATE: 'Duplicate',
  INCOMPLETE: 'Incomplete',
};

/** What a person needs to know about the CRM hand-off, in their words. */
export const FORWARD_LABEL: Record<LpsForwardState, string> = {
  PENDING: 'Sending',
  FORWARDED: 'In CRM',
  FAILED: 'Retrying',
  PARKED: 'Needs attention',
  SKIPPED: 'Not sent',
  AWAITING_ASSIGNMENT: 'Waiting for agent',
};

export const FORWARD_TONE: Record<LpsForwardState, NonNullable<UzBadgeProps['tone']>> = {
  PENDING: 'secondary',
  FORWARDED: 'success',
  FAILED: 'warning',
  PARKED: 'destructive',
  SKIPPED: 'outline',
  AWAITING_ASSIGNMENT: 'warning',
};

export const CHANNEL_LABEL: Record<LpsMarketingChannel, string> = {
  PAID_SOCIAL: 'Paid social',
  PAID_SEARCH: 'Paid search',
  PAID_OTHER: 'Paid (other)',
  ORGANIC_SOCIAL: 'Organic social',
  ORGANIC_SEARCH: 'Organic search',
  REFERRAL: 'Referral',
  DIRECT: 'Direct',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  OFFLINE: 'Offline',
};

export const DELIVERY_OUTCOME_LABEL: Record<LpsDeliveryOutcome, string> = {
  ACCEPTED: 'Lead stored',
  DUPLICATE_ONLY: 'Duplicate',
  NO_LEAD: 'No lead',
  HANDSHAKE: 'Handshake',
  REJECTED_SECRET: 'Wrong secret',
  REJECTED_SIGNATURE: 'Bad signature',
  CONNECTION_DISABLED: 'Paused',
  CONNECTOR_MISSING: 'Connector missing',
  INVALID_JSON: 'Not JSON',
  ERROR: 'Crashed',
};

export const DELIVERY_OUTCOME_TONE: Record<
  LpsDeliveryOutcome,
  NonNullable<UzBadgeProps['tone']>
> = {
  ACCEPTED: 'success',
  DUPLICATE_ONLY: 'secondary',
  NO_LEAD: 'warning',
  HANDSHAKE: 'outline',
  REJECTED_SECRET: 'destructive',
  REJECTED_SIGNATURE: 'destructive',
  CONNECTION_DISABLED: 'outline',
  CONNECTOR_MISSING: 'destructive',
  INVALID_JSON: 'destructive',
  ERROR: 'destructive',
};

export const ASSIGNMENT_METHOD_LABEL = {
  ROUND_ROBIN: 'Round-robin',
  MANUAL: 'Assigned by hand',
} as const;

export const formatDateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

/** 919876543210 -> +91 98765 43210 (Indian numbers only; others as +digits). */
export const formatPhone = (digits: string | null): string => {
  if (!digits) return '-';
  if (digits.length === 12 && digits.startsWith('91'))
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  return `+${digits}`;
};

/**
 * Leads a manager can hand to an agent from the queue: ones waiting for an
 * agent, and duplicates - skipped automatically, but a repeat enquiry can
 * deserve its own follow-up (it becomes a new CRM lead under the same customer).
 */
export const canBeAssigned = (lead: Pick<LpsLeadItem, 'status' | 'forward'>): boolean =>
  lead.forward.status === 'AWAITING_ASSIGNMENT' ||
  (lead.status === 'DUPLICATE' && lead.forward.status === 'SKIPPED');
